import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel, SectionHeading, SecondaryButton } from './ui/UI';
import { Eye, Target, Lock, AlertTriangle, Database, CheckCircle2, Clock, FileText, Power, MessageSquare, Server, Activity, Layers, Settings } from 'lucide-react';
const principles = [{
  icon: Eye,
  text: 'Read-only where possible'
}, {
  icon: Target,
  text: 'Scoped validation'
}, {
  icon: Lock,
  text: 'Customer-controlled permissions'
}, {
  icon: Settings,
  text: 'Clear test boundaries'
}, {
  icon: AlertTriangle,
  text: 'No destructive actions'
}, {
  icon: Database,
  text: 'No real data exfiltration'
}, {
  icon: CheckCircle2,
  text: 'Approval gates for sensitive missions'
}, {
  icon: Clock,
  text: 'Rate limits and test windows'
}, {
  icon: FileText,
  text: 'Audit logs'
}, {
  icon: Power,
  text: 'Kill switch'
}, {
  icon: MessageSquare,
  text: 'Plain-English evidence'
}, {
  icon: Activity,
  text: 'Transparent behavior'
}, {
  icon: Server,
  text: 'Optional internal runner'
}, {
  icon: Layers,
  text: 'Passive and active checks separated'
}];
const runnerCapabilities = ['Outbound-only communication', 'Scoped execution', 'Signed validation tasks', 'Policy-controlled testing', 'Rate limits', 'Audit logs', 'Local evidence collection', 'Internal reachability checks', 'Fix verification', 'BAS-lite & control-validation (planned)'];
export function Safety() {
  return <section className="relative w-full py-32 border-t border-white/5" data-id="element-779">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-780">
        <SectionLabel number="15" label="Safety" className="mb-8" data-id="element-781" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-782">
          <div className="lg:col-span-7" data-id="element-783">
            <SectionHeading size="xl" data-id="element-784">
              Built for safe validation.
            </SectionHeading>
          </div>
          <div className="lg:col-span-5 lg:pt-4" data-id="element-785">
            <p className="text-lg text-white/60 leading-relaxed" data-id="element-786">
              Periscan is designed for controlled validation, not reckless
              automation. The best validation product is not the loudest — it is
              the one customers trust enough to run continuously.
            </p>
          </div>
        </div>

        {/* Principles grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/5 mb-20" data-id="element-787">
          {principles.map((p, i) => <motion.div key={i} initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          delay: i * 0.03
        }} className="bg-[#070a0e] p-6 hover:bg-[#0a0d12] transition-colors" data-id="element-788">
              <div className="w-9 h-9 rounded-lg bg-[#1a8fd4]/10 border border-[#1a8fd4]/20 flex items-center justify-center mb-4" data-id="element-789">
                <p.icon className="w-4 h-4 text-[#1a8fd4]" data-id="element-790" />
              </div>
              <p className="text-sm text-white font-medium leading-snug" data-id="element-791">
                {p.text}
              </p>
            </motion.div>)}
        </div>

        {/* Internal Runner */}
        <div className="rounded-2xl bg-gradient-to-br from-[#0a0d12] to-[#070a0e] border border-white/5 p-8 md:p-12" data-id="element-792">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-793">
            <div className="lg:col-span-5" data-id="element-794">
              <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-4" data-id="element-795">
                Internal Runner
              </p>
              <h3 className="text-2xl md:text-3xl font-semibold text-white tracking-tight mb-4 leading-tight" data-id="element-796">
                Optional runner for
                <br data-id="element-797" />
                <span className="text-white/40" data-id="element-798">deeper validation.</span>
              </h3>
              <p className="text-base text-white/60 leading-relaxed mb-6" data-id="element-799">
                Some attack paths cannot be fully understood from the outside.
                Periscan's optional internal runner gives the platform a safe,
                scoped way to validate internal exposure, reachability, and
                remediation status.
              </p>
              <SecondaryButton href="#early-access" size="md" data-id="element-800">
                Join Internal Runner Waitlist
              </SecondaryButton>
              <p className="mt-4 text-xs text-white/40" data-id="element-801">
                The internal runner is planned for later beta phases.
              </p>
            </div>
            <div className="lg:col-span-7" data-id="element-802">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/5 rounded-xl overflow-hidden border border-white/5" data-id="element-803">
                {runnerCapabilities.map((cap, i) => <motion.div key={i} initial={{
                opacity: 0
              }} whileInView={{
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.3,
                delay: i * 0.04
              }} className="bg-[#050608] px-5 py-4 flex items-center gap-3" data-id="element-804">
                    <span className="font-mono text-xs text-[#1a8fd4]/60" data-id="element-805">
                      ▸
                    </span>
                    <span className="text-sm text-white/80" data-id="element-806">{cap}</span>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}